import React from 'react'

const SWATCH_DASH = { solid: 'none', dots: '0.1 8', dashed: '10 8' }
// Display labels for the line-style values (the stored value stays 'dots').
const STYLE_LABELS = { dots: 'Dotted', dashed: 'Dashed', solid: 'Solid' }

function rgba(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
  if (!m) return `rgba(255,0,0,${a})`
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`
}

/**
 * Shared Style controls — colour swatches, dotted/dashed/solid line style, and the
 * Regular/Bold/Heavy line-weight stepper. Used by both the track editor and the line
 * editor so they offer identical options. Styling relies on the .td-* classes in
 * track-details/index.scss (bundled globally).
 *
 * Free-tier gating (isPro=false locks non-free colours and non-dots styles) applies
 * to tracks; the line editor passes isPro=true so all options are available.
 */
const StyleControls = ({
  colors, color, onColor,
  lineStyles, lineStyle, onLineStyle,
  widths, lineWidth, onWidth,
  isPro = true, freeColor,
  // Shapes/circles omit the dotted/dashed/solid row (2D can't dash their borders).
  showLineStyle = true
}) => {
  const swatchStyle = (c) => {
    const selected = c === color
    const locked = !isPro && freeColor && c !== freeColor
    return {
      width: 20, height: 20, borderRadius: '30%',
      cursor: locked ? 'not-allowed' : 'pointer',
      background: locked ? rgba(c, 0.22) : rgba(c, selected ? 1 : 0.85),
      boxShadow: selected
        ? `0 0 0 2px #0d0d0d, 0 0 0 4px ${rgba(c, 0.9)}`
        : locked ? 'inset 0 0 0 1px rgba(255,255,255,0.08)' : 'inset 0 0 0 1px rgba(255,255,255,0.25)',
      transition: 'box-shadow .2s'
    }
  }
  const widthIdx = Math.max(0, (widths || []).findIndex(w => w.value === lineWidth))
  const widthCur = (widths && widths[widthIdx]) || { label: 'Regular' }

  return (
    <>
      <div className="td-swatches">
        {colors.map(c => (
          <div key={c} style={swatchStyle(c)} onClick={() => onColor(c)} />
        ))}
      </div>
      {showLineStyle && (
      <div className="td-linestyles">
        {lineStyles.map(s => {
          const active = s === lineStyle
          const locked = !isPro && s !== 'dots'
          const col = locked ? 'rgba(255,255,255,0.22)' : active ? color : '#c1c0c0'
          const labelCol = locked ? '#5a5a5a' : active ? '#ffffff' : '#c1c0c0'
          return (
            <div key={s} className="td-linestyle" style={{ cursor: locked ? 'not-allowed' : 'pointer' }}
              onClick={() => onLineStyle(s)}>
              <svg width="34" height="6">
                <line x1="1" y1="3" x2="33" y2="3" stroke={col} strokeWidth="2.5" strokeLinecap="round"
                  strokeDasharray={SWATCH_DASH[s]} />
              </svg>
              <span style={{ color: labelCol }}>{STYLE_LABELS[s] || (s.charAt(0).toUpperCase() + s.slice(1))}</span>
            </div>
          )
        })}
      </div>
      )}
      <div className="td-lineweight-row">
        <span className="td-lineweight-label">Line weight</span>
        <div className="td-lineweight-stepper">
          <span
            className={`td-lineweight-btn${widthIdx <= 0 ? ' td-lineweight-btn-off' : ''}`}
            onClick={() => { if (widthIdx > 0) onWidth(widths[widthIdx - 1].value) }}
          >−</span>
          <span className="td-lineweight-value">{widthCur.label}</span>
          <span
            className={`td-lineweight-btn${widthIdx >= widths.length - 1 ? ' td-lineweight-btn-off' : ''}`}
            onClick={() => { if (widthIdx < widths.length - 1) onWidth(widths[widthIdx + 1].value) }}
          >+</span>
        </div>
      </div>
    </>
  )
}

export default StyleControls
